import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { GoldenAdapter } from '@/adapters/golden.adapter';
import type {
  GoldenCase,
  GoldenCaseList,
  GoldenCaseListResponseDto,
  GoldenAnswerSummary,
  GoldenMe,
  GoldenProgress,
  GoldenRecord,
  GoldenTab,
} from '@/types/golden.types';

interface ApiEnvelope<T> {
  data: T;
}

const { GOLDEN } = API_ENDPOINTS.SENTINEL;

/** The Benchmark section's golden cases and the caller's own answers. Case ids only in URLs, never case text. */
export const goldenService = {
  async me(): Promise<GoldenMe> {
    const response = await api.get<ApiEnvelope<GoldenMe>>(GOLDEN.ME);
    return response.data.data;
  },

  async listCases(tab: GoldenTab): Promise<GoldenCaseList> {
    const response = await api.get<ApiEnvelope<GoldenCaseListResponseDto>>(GOLDEN.CASES, { params: { tab } });
    return GoldenAdapter.fromListApi(response.data.data, tab);
  },

  async getCase(caseId: string): Promise<GoldenCase> {
    const response = await api.get<ApiEnvelope<GoldenCase>>(GOLDEN.CASE(caseId));
    return response.data.data;
  },

  async getAnswer(caseId: string): Promise<GoldenRecord | null> {
    const response = await api.get<ApiEnvelope<{ record: GoldenRecord | null }>>(GOLDEN.ANSWER(caseId));
    return response.data.data.record;
  },

  async saveAnswer(caseId: string, record: GoldenRecord): Promise<void> {
    await api.put(GOLDEN.ANSWER(caseId), { record });
  },

  async myAnswers(): Promise<{ caseId: string; record: GoldenRecord }[]> {
    const response = await api.get<ApiEnvelope<{ caseId: string; record: GoldenRecord }[]>>(GOLDEN.MY_ANSWERS);
    return response.data.data;
  },

  async getReviewerState(): Promise<Record<string, unknown> | null> {
    const response = await api.get<ApiEnvelope<{ doc: Record<string, unknown> | null }>>(GOLDEN.REVIEWER_STATE);
    return response.data.data.doc;
  },

  async saveReviewerState(doc: Record<string, unknown>): Promise<void> {
    await api.put(GOLDEN.REVIEWER_STATE, { doc });
  },

  async progress(): Promise<GoldenProgress> {
    const response = await api.get<ApiEnvelope<GoldenProgress>>(GOLDEN.PROGRESS);
    return response.data.data;
  },

  /** Admins only: every Sentinel's answer to one case, summarised. */
  async answers(caseId: string): Promise<GoldenAnswerSummary[]> {
    const response = await api.get<ApiEnvelope<{ answers: GoldenAnswerSummary[] }>>(GOLDEN.ANSWERS(caseId));
    return response.data.data.answers;
  },
};
